/**
 * Single source of truth for toast notifications. EVERY success/error toast in
 * the app goes through here so the design is identical everywhere — only the
 * wording differs. Position is fixed by the global <Notifications> provider
 * (top-right), so toasts are visible from any page/modal.
 *
 * Usage:
 *   notifySuccess('User berhasil diperbarui');
 *   notifyError(err, 'Gagal memperbarui user');   // err → server reason as the body
 *   notifyError('Sesi Anda telah berakhir', 'Sesi berakhir'); // plain string body
 *
 * The global React Query MutationCache (main.tsx) calls these automatically for
 * every mutation, with per-action wording supplied via the mutation `meta`.
 */
import { notifications } from '@mantine/notifications';
import { IconCheck, IconAlertTriangle } from '@tabler/icons-react';
import { describeError } from '../api/client';

function activeLocale(): 'id' | 'en' {
  try {
    return localStorage.getItem('transpay_locale') === 'en' ? 'en' : 'id';
  } catch {
    return 'id';
  }
}

const BASE = { withBorder: true, radius: 'md' as const } as const;

/** Consistent green success toast. Defaults to a generic "saved" body. */
export function notifySuccess(message?: string, title?: string, id?: string): void {
  notifications.show({
    ...BASE,
    ...(id ? { id } : {}),
    color: 'teal',
    icon: <IconCheck size={18} />,
    autoClose: 3500,
    title,
    message:
      message ??
      (activeLocale() === 'en' ? 'Changes saved successfully' : 'Perubahan berhasil disimpan'),
  });
}

/**
 * Consistent red error toast. `messageOrError` may be a ready string body or an
 * error object (then the server reason is extracted via describeError). `title`
 * is the action-specific headline (e.g. "Gagal memperbarui user").
 */
export function notifyError(messageOrError?: unknown, title?: string, id?: string): void {
  const en = activeLocale() === 'en';
  const body =
    typeof messageOrError === 'string'
      ? messageOrError
      : describeError(messageOrError, en ? 'Please try again.' : 'Silakan coba lagi.');
  notifications.show({
    ...BASE,
    ...(id ? { id } : {}),
    color: 'red',
    icon: <IconAlertTriangle size={18} />,
    autoClose: 6000,
    title: title ?? (en ? 'Something went wrong' : 'Terjadi kesalahan'),
    message: body,
  });
}
